import { useState, useEffect, useCallback, useRef } from 'react';
import { Scale, Camera, Trash2, Loader2, GitCompareArrows, X, Plus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { memberApi, inputCls } from './memberApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const today = () => new Date().toISOString().slice(0, 10);

export const ProgressTab = () => {
  const [entries, setEntries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: today(), weight_lbs: '', waist_in: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState([]);
  const fileRef = useRef(null);

  const refresh = useCallback(() => {
    Promise.all([memberApi.get('/member/progress'), memberApi.get('/member/photos')])
      .then(([e, p]) => { setEntries(e.data); setPhotos(p.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await memberApi.post('/member/progress', {
        date: form.date,
        weight_lbs: form.weight_lbs === '' ? null : parseFloat(form.weight_lbs),
        waist_in: form.waist_in === '' ? null : parseFloat(form.waist_in),
        notes: form.notes,
      });
      setForm({ date: today(), weight_lbs: '', waist_in: '', notes: '' });
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save entry.');
    } finally { setSaving(false); }
  };

  const deleteEntry = async (id) => {
    await memberApi.delete(`/member/progress/${id}`).catch(() => {});
    refresh();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    if (file.size > 8 * 1024 * 1024) { setError('Photo must be under 8MB.'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await memberApi.post('/member/photos', fd);
      refresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Photo upload failed.');
    } finally { setUploading(false); }
  };

  const deletePhoto = async (id) => {
    await memberApi.delete(`/member/photos/${id}`).catch(() => {});
    setCompareIds(ids => ids.filter(x => x !== id));
    refresh();
  };

  const togglePhoto = (id) => {
    if (!compareMode) return;
    setCompareIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : ids.length < 2 ? [...ids, id] : [ids[1], id]);
  };

  const chartData = entries.filter(e => e.weight_lbs != null).map(e => ({ date: e.date.slice(5), weight: e.weight_lbs }));
  const comparePhotos = compareIds.map(id => photos.find(p => p.photo_id === id)).filter(Boolean);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#C41E3A]" size={30} /></div>;

  return (
    <div className="space-y-4" data-testid="progress-tab">
      {error && <p className="text-[#C41E3A] text-sm bg-[#C41E3A]/10 border border-[#C41E3A] rounded-sm p-3" data-testid="progress-error">{error}</p>}

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
        <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] flex items-center gap-2 mb-4"><Scale size={15} /> Log Progress</h3>
        <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="progress-date-input" />
          <input type="number" step="0.1" min="0" placeholder="Weight (lbs)" value={form.weight_lbs} onChange={e => setForm({ ...form, weight_lbs: e.target.value })} className={inputCls} data-testid="progress-weight-input" />
          <input type="number" step="0.1" min="0" placeholder="Waist (in)" value={form.waist_in} onChange={e => setForm({ ...form, waist_in: e.target.value })} className={inputCls} data-testid="progress-waist-input" />
          <button type="submit" disabled={saving} className="bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider rounded-sm transition-colors flex items-center justify-center gap-1.5 py-2.5" data-testid="progress-save-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Save
          </button>
          <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={`${inputCls} col-span-2 md:col-span-4`} data-testid="progress-notes-input" />
        </form>
      </div>

      {chartData.length >= 2 && (
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5" data-testid="progress-chart">
          <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-4">Weight Trend</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                <CartesianGrid stroke="#1a1a1a" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#555" tick={{ fontSize: 11 }} />
                <YAxis stroke="#555" tick={{ fontSize: 11 }} domain={['dataMin - 5', 'dataMax + 5']} />
                <Tooltip contentStyle={{ background: '#121212', border: '1px solid #27272A', borderRadius: 2, fontSize: 12 }} labelStyle={{ color: '#D4AF37' }} itemStyle={{ color: '#fff' }} />
                <Line type="monotone" dataKey="weight" stroke="#C41E3A" strokeWidth={2} dot={{ fill: '#D4AF37', r: 3 }} name="lbs" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
          <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] mb-3">History</h3>
          {[...entries].reverse().map(e => (
            <div key={e.entry_id} className="flex items-center justify-between py-2 border-b border-[#1a1a1a] last:border-0 text-sm" data-testid={`progress-entry-${e.entry_id}`}>
              <div>
                <span className="text-gray-400 text-xs mr-3">{e.date}</span>
                {e.weight_lbs != null && <span className="text-white mr-3">{e.weight_lbs} lbs</span>}
                {e.waist_in != null && <span className="text-gray-300 mr-3">Waist {e.waist_in}"</span>}
                {e.notes && <span className="text-gray-500 text-xs">{e.notes}</span>}
              </div>
              <button onClick={() => deleteEntry(e.entry_id)} className="text-gray-600 hover:text-[#C41E3A] transition-colors" data-testid={`progress-delete-${e.entry_id}`}><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-[#27272A] rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-['Oswald'] text-sm uppercase tracking-[0.15em] text-[#D4AF37] flex items-center gap-2"><Camera size={15} /> Progress Photos</h3>
          <div className="flex gap-2">
            {photos.length >= 2 && (
              <button onClick={() => { setCompareMode(!compareMode); setCompareIds([]); }} className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${compareMode ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#27272A] text-gray-400 hover:text-white'}`} data-testid="compare-toggle-btn">
                <GitCompareArrows size={12} /> {compareMode ? 'Done' : 'Compare'}
              </button>
            )}
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors" data-testid="photo-upload-btn">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Add Photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="photo-file-input" />
          </div>
        </div>

        {compareMode && comparePhotos.length === 2 && (
          <div className="grid grid-cols-2 gap-3 mb-4" data-testid="photo-compare-view">
            {comparePhotos.map(p => (
              <div key={p.photo_id}>
                <img src={`${API}/member/photos/${p.photo_id}/image`} alt={p.taken_at} className="w-full max-h-96 object-contain bg-black rounded-sm border border-[#D4AF37]/50" />
                <p className="text-[#D4AF37] text-xs text-center mt-1.5">{p.taken_at}</p>
              </div>
            ))}
          </div>
        )}
        {compareMode && comparePhotos.length < 2 && <p className="text-gray-500 text-xs mb-3">Select 2 photos below to compare side by side.</p>}

        {photos.length === 0 ? (
          <p className="text-gray-500 text-sm">No photos yet. Add your first progress photo to start tracking visually.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {photos.map(p => (
              <div key={p.photo_id} className="relative group cursor-pointer" onClick={() => togglePhoto(p.photo_id)} data-testid={`photo-${p.photo_id}`}>
                <img src={`${API}/member/photos/${p.photo_id}/image`} alt={p.taken_at} className={`w-full aspect-square object-cover rounded-sm border transition-colors ${compareIds.includes(p.photo_id) ? 'border-[#D4AF37]' : 'border-[#27272A]'}`} />
                <span className="absolute bottom-1 left-1 bg-black/70 text-gray-300 text-[10px] px-1.5 py-0.5 rounded-sm">{p.taken_at}</span>
                {!compareMode && (
                  <button onClick={(e) => { e.stopPropagation(); deletePhoto(p.photo_id); }} className="absolute top-1 right-1 bg-black/70 text-gray-400 hover:text-[#C41E3A] p-1 rounded-sm opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity" data-testid={`photo-delete-${p.photo_id}`}>
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
